import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import API from '../services/api';
import { resolveProfilePhoto } from '../utils/profilePhoto';
import './ContractorPages.css';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—');
const emptyBagRow = () => ({ bagSize: '', bagCount: '' });
const bagSummary = (rows, unit) => {
  const summary = Array.isArray(rows) ? rows.map((row) => `${row.bagSize}`).join(', ') : '';
  return summary || String(unit || '').trim() || '—';
};
const statusLabel = (value) => {
  if (value === 'plant_approved') return 'Plant Approved';
  if (value === 'plant_rejected') return 'Plant Rejected';
  return 'Pending Plant Approval';
};
const queryStatusLabel = (value) => {
  if (value === 'open') return 'Open Query';
  if (value === 'clarified') return 'Clarified';
  return '';
};
const approvalByLabel = (item) => {
  const name = item?.plantApprovedBy?.fullName || '';
  return name || '—';
};
const roleLabel = (role) => {
  if (role === 'plant_manager') return 'Plant Manager';
  if (role === 'contractor') return 'Contractor';
  return role || 'User';
};
const normalizeBagRows = (rows) => (
  Array.isArray(rows) && rows.length
    ? rows.map((row) => ({
      bagSize: String(row?.bagSize || ''),
      bagCount: '',
    }))
    : [emptyBagRow()]
);
const shouldSyncAutoBagSize = (rows, previousAutoUnit) => {
  if (!Array.isArray(rows) || rows.length !== 1) return false;
  const row = rows[0] || {};
  const bagSize = String(row?.bagSize || '').trim();
  if (!bagSize) return true;
  return !!previousAutoUnit && bagSize === previousAutoUnit;
};
const canEditEntry = (item) => String(item?.workflowStatus || '') !== 'plant_approved';

const ContractorWorkHistory = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [clarificationRow, setClarificationRow] = useState(null);
  const [lastAutoBagUnit, setLastAutoBagUnit] = useState('');
  const [editForm, setEditForm] = useState({
    workerName: '',
    workType: '',
    quantity: 1,
    bagDetails: [emptyBagRow()],
    workDate: '',
    remarks: '',
  });
  const [clarificationForm, setClarificationForm] = useState({
    scope: 'accounts',
    message: '',
    photos: [],
  });
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    workType: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [rowsRes, ratesRes] = await Promise.all([
        API.get('/contractor/work-entries', { params: filters }),
        API.get('/contractor/work-rates'),
      ]);
      setRows(Array.isArray(rowsRes.data) ? rowsRes.data : []);
      setRates(Array.isArray(ratesRes.data) ? ratesRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load work history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const onEditChange = (e) => {
    const { name, value } = e.target;
    if (name === 'quantity') {
      setEditForm((prev) => ({ ...prev, quantity: String(value || '').replace(/\D/g, '') }));
      return;
    }
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const onBagDetailChange = (index, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      bagDetails: prev.bagDetails.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      )),
    }));
  };

  const addBagRow = () => {
    setEditForm((prev) => ({ ...prev, bagDetails: [...prev.bagDetails, emptyBagRow()] }));
  };

  const removeBagRow = (index) => {
    setEditForm((prev) => {
      const nextRows = prev.bagDetails.filter((_, rowIndex) => rowIndex !== index);
      return {
        ...prev,
        bagDetails: nextRows.length ? nextRows : [emptyBagRow()],
      };
    });
  };

  const openEdit = (item) => {
    if (!canEditEntry(item)) return;
    setEditingRow(item);
    setEditForm({
      workerName: item.workerName || '',
      workType: item.workType || '',
      quantity: String(item.quantity ?? 1),
      bagDetails: normalizeBagRows(item.bagDetails),
      workDate: item.workDate ? new Date(item.workDate).toISOString().slice(0, 10) : '',
      remarks: item.remarks || '',
    });
    setLastAutoBagUnit('');
    setError('');
    setMessage('');
  };

  const closeEdit = () => {
    setEditingRow(null);
    setSaving(false);
  };

  const openClarification = (item) => {
    const scope = item?.accountsQueryNote ? 'accounts' : 'hr';
    setClarificationRow(item);
    setClarificationForm({
      scope,
      message: '',
      photos: [],
    });
    setError('');
    setMessage('');
  };

  const closeClarification = () => {
    setClarificationRow(null);
    setSaving(false);
  };

  const selectedRate = useMemo(
    () => rates.find((row) => row.workType === editForm.workType) || null,
    [rates, editForm.workType]
  );

  useEffect(() => {
    if (!editingRow) return;

    const defaultBagSize = String(selectedRate?.unit || '').trim();
    if (!defaultBagSize || defaultBagSize.toLowerCase() === 'job') return;

    setEditForm((prev) => {
      if (!shouldSyncAutoBagSize(prev.bagDetails, lastAutoBagUnit)) return prev;
      return {
        ...prev,
        bagDetails: [{ bagSize: defaultBagSize, bagCount: '' }],
      };
    });
    setLastAutoBagUnit(defaultBagSize);
  }, [editingRow, selectedRate, lastAutoBagUnit]);

  const effectiveQuantity = Number(editForm.quantity || 0);
  const estAmount = effectiveQuantity * Number(selectedRate?.rate || 0);

  const saveEdit = async () => {
    if (!editingRow?._id) return;

    try {
      setSaving(true);
      setError('');
      setMessage('');

      const normalizedBagDetails = (editForm.bagDetails || []).map((row) => ({
        bagSize: String(row.bagSize || '').trim(),
        bagCount: 0,
      }));
      const hasPartialBagRow = normalizedBagDetails.some((row) => row.bagSize === '');
      if (hasPartialBagRow) {
        throw new Error('Bag size sahi bharo, ya row hata do');
      }

      await API.put(`/contractor/work-entries/${editingRow._id}`, {
        workerName: editForm.workerName,
        workType: editForm.workType,
        quantity: effectiveQuantity,
        bagDetails: normalizedBagDetails.filter((row) => row.bagSize),
        workDate: editForm.workDate,
        remarks: editForm.remarks,
      });

      setMessage('Entry update ho gayi. Ab ye dubara Plant approval me chali gayi hai.');
      closeEdit();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update work entry');
    } finally {
      setSaving(false);
    }
  };

  const saveClarification = async () => {
    if (!clarificationRow?._id) return;

    try {
      setSaving(true);
      setError('');
      setMessage('');

      const data = new FormData();
      data.append('scope', clarificationForm.scope);
      data.append('message', clarificationForm.message);
      Array.from(clarificationForm.photos || []).forEach((file) => data.append('photos', file));

      await API.post(`/contractor/work-entries/${clarificationRow._id}/clarifications`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessage('Clarification submit ho gayi.');
      closeClarification();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to submit clarification');
    } finally {
      setSaving(false);
    }
  };

  const totalAmount = useMemo(
    () => rows.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0),
    [rows]
  );

  return (
    <Card className="contractor-card">
      <Card.Body>
        <h4 className="mb-3">Work History</h4>
        {message && <Alert variant="success">{message}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="g-2 mb-3">
          <Col md={3}>
            <Form.Control type="date" name="from" value={filters.from} onChange={onChange} />
          </Col>
          <Col md={3}>
            <Form.Control type="date" name="to" value={filters.to} onChange={onChange} />
          </Col>
          <Col md={4}>
            <Form.Control name="workType" placeholder="Work type" value={filters.workType} onChange={onChange} />
          </Col>
          <Col md={2} className="d-grid">
            <Button onClick={load}>Filter</Button>
          </Col>
        </Row>

        <div className="mb-2 text-muted">Total Amount: ₹{totalAmount.toLocaleString('en-IN')}</div>

        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : rows.length === 0 ? (
          <div className="text-muted">No entries found.</div>
        ) : (
          <>
            <div className="contractor-table-wrap">
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Worker</th>
                    <th>Work Type</th>
                    <th>No. Of Bags</th>
                    <th>Bag Details</th>
                    <th>Rate</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Review Query</th>
                    <th>Clarifications</th>
                    <th>Approved By</th>
                    <th>Approved At</th>
                    <th>Remarks</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={item._id}>
                      <td>{fmtDate(item.workDate)}</td>
                      <td>{item.workerName}</td>
                      <td>{item.workType}</td>
                      <td>{item.quantity}</td>
                      <td>{bagSummary(item.bagDetails, item.unit)}</td>
                      <td>₹{Number(item.unitRate || 0).toLocaleString('en-IN')}</td>
                      <td>₹{Number(item.totalAmount || 0).toLocaleString('en-IN')}</td>
                      <td>{statusLabel(item.workflowStatus)}</td>
                      <td>
                        {item.accountsQueryNote ? <div className="small">Accounts: {item.accountsQueryNote} {queryStatusLabel(item.accountsQueryStatus) ? `(${queryStatusLabel(item.accountsQueryStatus)})` : ''}</div> : null}
                        {item.hrQueryNote ? <div className="small">HR: {item.hrQueryNote} {queryStatusLabel(item.hrQueryStatus) ? `(${queryStatusLabel(item.hrQueryStatus)})` : ''}</div> : null}
                        {!item.accountsQueryNote && !item.hrQueryNote ? '—' : null}
                      </td>
                      <td>
                        {Array.isArray(item.clarifications) && item.clarifications.length ? (
                          <div className="contractor-inline-stack">
                            {item.clarifications.map((clar, index) => (
                              <div className="contractor-query-block" key={`${item._id}-clar-${index}`}>
                                <strong>{roleLabel(clar.submittedRole)}</strong>
                                {clar.message ? <div>{clar.message}</div> : null}
                                <div className="small text-muted">{fmtDateTime(clar.createdAt)}</div>
                                {Array.isArray(clar.photos) && clar.photos.length ? (
                                  <div className="contractor-clar-photo-row">
                                    {clar.photos.map((photo, photoIndex) => {
                                      const src = resolveProfilePhoto(photo, clar.createdAt || photoIndex);
                                      return (
                                        <a href={src} target="_blank" rel="noreferrer" key={`${item._id}-clar-photo-${photoIndex}`}>
                                          <img src={src} alt="clarification" className="contractor-clar-photo" />
                                        </a>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : '—'}
                      </td>
                      <td>{approvalByLabel(item)}</td>
                      <td>{fmtDateTime(item.plantApprovedAt)}</td>
                      <td>{item.remarks || '—'}</td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          {canEditEntry(item) ? (
                            <Button size="sm" variant="outline-primary" onClick={() => openEdit(item)}>Edit</Button>
                          ) : (
                            <span className="text-muted small">Locked</span>
                          )}
                          {(item.accountsQueryNote || item.hrQueryNote) ? (
                            <Button size="sm" variant="outline-warning" onClick={() => openClarification(item)}>Submit Clarity</Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="contractor-mobile-list">
              {rows.map((item) => (
                <div className="contractor-mobile-item" key={item._id}>
                  <div className="title">{item.workerName} • {item.workType}</div>
                  <div className="meta">{fmtDate(item.workDate)} | {item.quantity} bags</div>
                  <div className="meta">Bags: {bagSummary(item.bagDetails, item.unit)}</div>
                  <div className="meta">₹{Number(item.totalAmount || 0).toLocaleString('en-IN')}</div>
                  <div className="meta">{statusLabel(item.workflowStatus)}</div>
                  {item.accountsQueryNote ? <div className="meta">Accounts Query: {item.accountsQueryNote}</div> : null}
                  {item.hrQueryNote ? <div className="meta">HR Query: {item.hrQueryNote}</div> : null}
                  {Array.isArray(item.clarifications) && item.clarifications.length ? (
                    <div className="meta">Clarifications: {item.clarifications.length}</div>
                  ) : null}
                  <div className="meta">Approved By: {approvalByLabel(item)}</div>
                  <div className="meta">Approved At: {fmtDateTime(item.plantApprovedAt)}</div>
                  {item.remarks ? <div className="meta">{item.remarks}</div> : null}
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {canEditEntry(item) ? (
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(item)}>Edit Entry</Button>
                    ) : null}
                    {(item.accountsQueryNote || item.hrQueryNote) ? (
                      <Button size="sm" variant="outline-warning" onClick={() => openClarification(item)}>Submit Clarity</Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card.Body>

      <Modal show={!!editingRow} onHide={closeEdit} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Work Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Worker Name</Form.Label>
                <Form.Control name="workerName" value={editForm.workerName} onChange={onEditChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Work Type</Form.Label>
                <Form.Select name="workType" value={editForm.workType} onChange={onEditChange}>
                  {rates.map((rate) => (
                    <option key={rate._id} value={rate.workType}>{rate.workType}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>No. Of Bags</Form.Label>
                <Form.Control type="text" inputMode="numeric" pattern="[0-9]*" name="quantity" value={editForm.quantity} onChange={onEditChange} />
                <div className="form-text">
                  Direct no. of bags dalo. Isi hisaab se amount banega.
                </div>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Work Date</Form.Label>
                <Form.Control type="date" name="workDate" value={editForm.workDate} onChange={onEditChange} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Estimated Amount</Form.Label>
                <Form.Control value={`₹${Number(estAmount || 0).toLocaleString('en-IN')}`} disabled />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                  <Form.Label className="mb-0">Bag Breakdown</Form.Label>
                  <Button type="button" variant="outline-secondary" size="sm" onClick={addBagRow}>Add Bag Size</Button>
                </div>
                    <div className="contractor-bag-grid">
                      {editForm.bagDetails.map((row, index) => (
                        <div className="contractor-bag-row" key={`edit-bag-${index}`}>
                          <Form.Control
                            placeholder="Bag size, e.g. 5kg"
                            value={row.bagSize}
                            onChange={(e) => onBagDetailChange(index, 'bagSize', e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeBagRow(index)}
                            disabled={editForm.bagDetails.length === 1}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="form-text">Yahan sirf bag sizes dalo, jaise 5kg, 30kg.</div>
                  </Form.Group>
                </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Remarks</Form.Label>
                <Form.Control as="textarea" rows={3} name="remarks" value={editForm.remarks} onChange={onEditChange} />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeEdit} disabled={saving}>Cancel</Button>
          <Button onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!clarificationRow} onHide={closeClarification} centered>
        <Modal.Header closeButton>
          <Modal.Title>Submit Clarification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>For</Form.Label>
            <Form.Select
              value={clarificationForm.scope}
              onChange={(e) => setClarificationForm((prev) => ({ ...prev, scope: e.target.value }))}
            >
              {clarificationRow?.accountsQueryNote ? <option value="accounts">Accounts</option> : null}
              {clarificationRow?.hrQueryNote ? <option value="hr">HR</option> : null}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={clarificationForm.message}
              onChange={(e) => setClarificationForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Issue ki clarity likho"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Photos</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setClarificationForm((prev) => ({ ...prev, photos: Array.from(e.target.files || []) }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeClarification} disabled={saving}>Cancel</Button>
          <Button onClick={saveClarification} disabled={saving || (!clarificationForm.message.trim() && !(clarificationForm.photos || []).length)}>
            {saving ? 'Saving...' : 'Submit'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ContractorWorkHistory;
